import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../AuthProvider';
import { ArrowLeft, RefreshCw, CheckCircle, Clock, XCircle, AlertCircle, Calendar, FileText, Tag } from 'lucide-react';
import AdminNav from './AdminNav';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [appRoles, setAppRoles] = useState([]);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!token) {
        setError('Authentication token not available. Please log in again.');
        setLoading(false);
        return;
      }
      // Explicitly set Authorization header as fallback
      const response = await axios.get(`${API_BASE}/admin/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUser(response.data);
      setTasks(response.data.tasks || []);
      setSelectedRole(response.data.role || 'user');
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.detail || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && token) {
      fetchUserDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run on userId/token change only; fetchUserDetails is stable
  }, [userId, token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/admin/roles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAppRoles(res.data?.roles || []))
      .catch(() => setAppRoles([]));
  }, [token]);

  const handleUpdateRole = async () => {
    try {
      if (!token) {
        alert('Authentication token not available. Please log in again.');
        return;
      }
      if (!selectedRole) {
        alert('Please select a role');
        return;
      }
      setUpdatingRole(true);
      // Explicitly set Authorization header as fallback
      await axios.patch(
        `${API_BASE}/admin/users/${userId}/role`,
        {
          role: selectedRole
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      await fetchUserDetails();
      alert('Role updated successfully');
    } catch (err) {
      console.error('Error updating role:', err);
      alert(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetPasswordError('');
    if (!resetPassword || resetPassword.length < 8) {
      setResetPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setResetPasswordError('Passwords do not match.');
      return;
    }
    try {
      setResettingPassword(true);
      await axios.post(
        `${API_BASE}/admin/users/${userId}/reset-password`,
        { new_password: resetPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetPassword('');
      setResetPasswordConfirm('');
      alert('Password reset successfully. Share the new password with the user securely.');
    } catch (err) {
      setResetPasswordError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      if (!token) {
        alert('Authentication token not available. Please log in again.');
        return;
      }
      // Explicitly set Authorization header as fallback
      await axios.patch(
        `${API_BASE}/admin/tasks/${taskId}`,
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      await fetchUserDetails();
    } catch (err) {
      console.error('Error updating task:', err);
      alert(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-neutral-950">
        <div className="text-xl font-semibold">Loading user details...</div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-neutral-950 p-4">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-medium transition-colors"
        >
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 dark:bg-neutral-950 p-8">
      <div className="max-w-7xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-medium text-gray-900 dark:text-neutral-100">User Details</h1>
              <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">
                {user?.email || `User ID: ${user?.user_id?.substring(0, 8)}...`}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <button
              onClick={fetchUserDetails}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium whitespace-nowrap border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        <AdminNav />

        {/* User Info Card */}
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md border border-gray-200 dark:border-neutral-800 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-4">User Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400">Name</p>
              <p className="text-lg font-medium text-gray-900 dark:text-neutral-100">{user?.name || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400">Email</p>
              <p className="text-lg font-medium text-gray-900 dark:text-neutral-100">{user?.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400 mb-2">Role</p>
              <div className="flex items-center space-x-2">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="text-sm border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-2 bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100"
                >
                  {(appRoles.length ? appRoles : [
                    { code: 'user', display_name: 'user' },
                    { code: 'contributor', display_name: 'Contributor' },
                    { code: 'reviewer', display_name: 'Reviewer' },
                    { code: 'pm', display_name: 'PM' },
                    { code: 'admin', display_name: 'Admin' },
                  ]).map((r) => (
                    <option key={r.code} value={r.code}>{r.display_name || r.code}</option>
                  ))}
                </select>
                <button
                  onClick={handleUpdateRole}
                  disabled={updatingRole || selectedRole === user?.role}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {updatingRole ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-neutral-700">
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400">Projects</p>
              <p className="text-lg font-medium text-gray-900 dark:text-neutral-100">{user?.project_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400">Total Tasks</p>
              <p className="text-lg font-medium text-gray-900 dark:text-neutral-100">{user?.task_count || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-neutral-400">Account Created</p>
              <p className="text-lg font-medium text-gray-900 dark:text-neutral-100">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Password Reset - only for users with auth account */}
        {user?.auth_user_id && (
          <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md border border-gray-200 dark:border-neutral-800 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-4">Reset Password</h2>
            <form onSubmit={handleResetPassword} className="max-w-md space-y-4">
              {resetPasswordError && (
                <p className="text-sm text-red-600 dark:text-red-400">{resetPasswordError}</p>
              )}
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100"
                  placeholder="Min 8 characters"
                  minLength={8}
                  disabled={resettingPassword}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label htmlFor="reset-password-confirm" className="block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1">Confirm new password</label>
                <input
                  id="reset-password-confirm"
                  type="password"
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100"
                  placeholder="Confirm"
                  disabled={resettingPassword}
                  autoComplete="new-password"
                />
              </div>
              <button
                type="submit"
                disabled={resettingPassword}
                className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {resettingPassword ? 'Resetting...' : 'Reset password'}
              </button>
            </form>
          </div>
        )}

        {/* Tasks Section */}
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md border border-gray-200 dark:border-neutral-800 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-neutral-100">Assigned Tasks</h2>
          </div>

          {/* Tasks List */}
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-neutral-400">
              No tasks assigned to this user
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="border border-gray-200 dark:border-neutral-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-neutral-100">{task.title}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                          {getStatusIcon(task.status)}
                          <span className="ml-1.5 capitalize">{task.status}</span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          <span className="capitalize">{task.priority}</span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-neutral-400 mb-2">
                        <span className="flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          {task.task_type.replace('_', ' ')}
                        </span>
                        {task.due_date && (
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Due: {new Date(task.due_date).toLocaleString()}
                          </span>
                        )}
                        <span className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          Assigned: {new Date(task.assigned_at).toLocaleDateString()}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-700 dark:text-neutral-300 mb-2">{task.description}</p>
                      )}
                      {task.notes && (
                        <p className="text-xs text-gray-500 dark:text-neutral-400 italic">Notes: {task.notes}</p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-2">
                      {task.status !== 'completed' && task.status !== 'cancelled' && (
                        <select
                          value={task.status}
                          onChange={(e) => handleUpdateTaskStatus(task.id, e.target.value)}
                          className="text-sm border border-gray-300 dark:border-neutral-700 rounded-md px-2 py-1 bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100"
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
